// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement } from '../../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import Icon, { ICON_SIZE_16 } from '../Icon';
import Spinner from '../Spinner';

import '../../helpers/base.less';
import './Action.less';


/**
 * `Action` component.
 */
const Action = ({ baseClassName, component: Tag, icon, rightAddon, bottomAddon, children, progress, nowrap, outerProps, ...props }) => {
    const { className, ...restOuterProps } = outerProps;
    icon = Icon.create(icon, {
        className: `${baseClassName}__icon`,
        size: ICON_SIZE_16,
    });
    const size = icon && icon.props.size ? icon.props.size : ICON_SIZE_16;
    if (progress) {
        icon = <Icon size={size} className={`${baseClassName}__icon`}><Spinner /></Icon>;
    }

    return (
        <div
            className={classNames(
                baseClassName,
                {
                    [`${baseClassName}--size-${size}`]: icon || progress,
                    [`${baseClassName}--progress`]: progress,
                    [`${baseClassName}--nowrap`]: nowrap,
                    [`${baseClassName}--icon`]: !children && !rightAddon && !bottomAddon,
                },
                className,
            )}
            {...restOuterProps}
        >
            <Tag {...props}>
                {icon}
                {children}
            </Tag>
            {rightAddon ? <div className={`${baseClassName}__right-addon`}>{rightAddon}</div> : null}
            {bottomAddon ? <div className={`${baseClassName}__bottom-addon`}>{bottomAddon}</div> : null}
        </div>
    );
};

Action.propTypes = {
    /**
     * Content of the `Action`.
     */
    children: PropTypes.node,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
    /**
     * Useful when rendering an `Action` as `<Link>` instead `<a>`.
     */
    component: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func,
    ]),
    /**
     * Icon of the Action.
     */
    icon: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.element,
    ]),
    /**
     * Right additional content.
     */
    rightAddon: PropTypes.node,
    /**
     * Bottom additional content.
     */
    bottomAddon: PropTypes.node,
    /**
     * Indicate the progress state of the `Action`.
     */
    progress: PropTypes.bool,
    /**
     * Suppresses line breaks (text wrapping) within the source.
     */
    nowrap: PropTypes.bool,
    /**
     * Props for the outer element.
     */
    outerProps: PropTypes.object,
};

Action.defaultProps = {
    children: null,
    baseClassName: `${CLS_PREFIX}action`,
    component: 'a',
    icon: null,
    rightAddon: null,
    bottomAddon: null,
    progress: false,
    nowrap: false,
    outerProps: {},
};

export default Action;



// WEBPACK FOOTER //
// ./components/Action/Action.js